import React from 'react';
import { twMerge } from 'tailwind-merge';
import { gql } from '@soundxyz/gql-string';
import { BackerPopup } from '@components/BackerPopup';
import { TopCommentCard } from '@components/CommentCard/TopCommentCard';
import { getImageSize, getRow } from '@components/Song/Audience/helpers';
import {
  SECOND_THIRD_ROW_DESKTOP,
  SECOND_THIRD_ROW_MOBILE,
} from '@components/Song/Audience/shared/constants';
import { GoldenEggContainer, Seat } from '@components/Song/Audience/shared/styles';
import { GoldenEgg } from '@components/basics/GoldenEgg';
import { Avatar } from '@components/basics/Images/Avatar';
import { AccessibleLink } from '@components/main-components/link/AccessibleLink';
import { DisplayToggle } from '@components/shared';
import { useMediaQuery } from '@context/mediaQuery';
import type { FragmentType } from '@graphql/reactQuery';
import { getFragment } from '@graphql/reactQuery';
import { TopAudienceSeatFragmentDoc } from '@graphql/reactQuery';
import { webappUriPath } from '@utils/url';
import { RankCard, showRank } from './RankCard';

/*#__PURE__*/ gql(/* GraphQL */ `
  fragment TopAudienceSeat on ReleaseCollector {
    id
    vipNftsCount
    user {
      id
      username
      publicAddress
      webappUri
      avatar {
        id
        url
      }
    }
    ownedGoldenEgg {
      id
    }
    ...RankCard
    ...TopCommentCard
  }
`);

export const AudienceSeat = ({
  seatFrag,
  index,
  last = false,
}: {
  seatFrag: FragmentType<TopAudienceSeatFragmentDoc>;
  index: number;
  last?: boolean;
}) => {
  const seat = getFragment(TopAudienceSeatFragmentDoc, seatFrag);

  const {
    ownedGoldenEgg,
    user: { avatar, username, publicAddress, webappUri },
  } = seat;

  const { tablet, tabletLandscape, laptop } = useMediaQuery();
  const midRow = tablet ? SECOND_THIRD_ROW_DESKTOP : SECOND_THIRD_ROW_MOBILE;

  const seatPos = last
    ? 'last'
    : getRow({
        index,
        midRowMax: midRow,
      });

  const hasGoldenEgg = !!ownedGoldenEgg;

  const userSeat = (
    <>
      {hasGoldenEgg && (
        <GoldenEggContainer row={seatPos}>
          <GoldenEgg />
        </GoldenEggContainer>
      )}
      <Avatar
        size={getImageSize({ desktopBreakpoint: laptop, rowPos: seatPos })}
        disableLink={!tabletLandscape}
        src={avatar?.url}
        username={username}
        walletAddress={publicAddress}
        borderRadius={8}
        webappUri={webappUri}
        priority={false}
      />

      {!last && showRank({ rank: index }) && <RankCard rankFrag={seat} rank={index + 1} />}
    </>
  );

  return (
    <BackerPopup key={`top-backer-popup-${index}`} content={<TopCommentCard data={seat} />}>
      <Seat className={seatPos}>
        <DisplayToggle media="notTabletLandscape">{userSeat}</DisplayToggle>
        <DisplayToggle media="tabletLandscape">
          <AccessibleLink pathnameUri={webappUriPath(webappUri)}>{userSeat}</AccessibleLink>
        </DisplayToggle>
      </Seat>
    </BackerPopup>
  );
};

export const EmptySeat = ({ index }: { index: number }) => {
  const { tablet } = useMediaQuery();
  const midRow = tablet ? SECOND_THIRD_ROW_DESKTOP : SECOND_THIRD_ROW_MOBILE;

  const row = getRow({
    index,
    midRowMax: midRow,
  });
  return (
    <div
      className={twMerge(
        row,
        'relative col-span-3 aspect-square h-auto w-full ',
        'animate-pulse rounded-md bg-neutral100 md:col-span-2',
      )}
      key={`empty-seat-${index}`}
    />
  );
};
